import React from "react";
import { Route, Switch } from "react-router-dom";

import * as QueriesQL from "queries/page";
import PagePublicSchema from "@common/schema/page/public";
import * as Section from "@repoflow/components/section";
import { Crumb, useCrumbLabel } from "@repoflow/components/breadcrumb";

import * as ViewRoute from "./view";
import * as EditRoute from "./edit";
import * as RemoveRoute from "./remove";
import * as TranslateRoute from "./translate";
import * as ImportRoute from "./import";

const LocalProvider = ({
  match: {
    params,
    params: { pageId },
  },
  children: Children,
}) => {
  const { base: parentBase, basepath: parentBasepath } =
    Section.useSectionContext();

  const base = `${parentBase}/${pageId}`;
  const basepath = `${parentBasepath}/:pageId`;

  // Keyed on the entity, which is what the accidental remount used to do.
  //
  // Every screen below seeds its form state ONCE, from the entity this
  // route resolved. Until the render prop above was called rather than
  // rendered, React saw a new component type on every render and remounted
  // the lot — which reset those forms, and happened to be what made moving
  // from one pageId to another show the new one's values. It no longer
  // remounts for free, so the reset it was providing is asked for here.
  return (
    <Section.Provider
      key={pageId}
      schema={PagePublicSchema}
      base={base}
      basepath={basepath}
      attributes={params}
    >
      {/* Called, never rendered as an element — see Section.Provider. */}
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export function Content(props) {
  return (
    <LocalProvider {...props}>
      {({ params }) => (
        <Section.Query
          query={QueriesQL.get}
          variables={params}
          datapath={"sites.get.pages.get"}
        >
          {ContentHandler}
        </Section.Query>
      )}
    </LocalProvider>
  );
}

function ContentHandler({ basepath }) {
  return (
    <Switch>
      <Route path={`${basepath}/remove`} component={RemoveRoute.Content} />
      <Route path={`${basepath}/translate`} component={TranslateRoute.Content} />
      {/* The UPDATE verb of the bundle import — this page's data, put back.
          The listing's /import is the CREATE verb; see ../import. */}
      <Route path={`${basepath}/import`} component={ImportRoute.Content} />
      <Route path={`${basepath}/edit`} component={EditRoute.Content} />
      <Route path={`${basepath}`} component={ViewRoute.Content} />
    </Switch>
  );
}

// The id is generated and says nothing; the title is what the page is called
// everywhere else on the screen.
function PageCrumb({ base, siteId, pageId }) {
  const label = useCrumbLabel({
    query: QueriesQL.get,
    variables: { siteId, pageId },
    datapath: "sites.get.pages.get.title",
    fallback: pageId,
  });

  return <Crumb to={`${base}`} label={label} />;
}

export function Indice(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath, params: { siteId, pageId } }) => (
        <>
          <PageCrumb base={base} siteId={siteId} pageId={pageId} />

          <Switch>
            <Route path={`${basepath}/remove`} component={RemoveRoute.Indice} />
            {/* The crumb the translate screen has always exported and nothing
                rendered: without a Route here the trail stopped at the page,
                so the one screen that creates a DIFFERENT page was the one
                screen whose breadcrumb did not say where you were. */}
            <Route path={`${basepath}/translate`} component={TranslateRoute.Indice} />
            <Route path={`${basepath}/import`} component={ImportRoute.Indice} />
            <Route path={`${basepath}/edit`} component={EditRoute.Indice} />
            <Route path={`${basepath}`} component={ViewRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

export const Sidebar = () => (
  <>
    <EditRoute.Sidebar />
    <ViewRoute.Sidebar />
  </>
);
